import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { formatSlope } from '../../../../../utils/math/slope';
import { isDefined } from '../../../../../utils/variables';
import { DesignHoverMetadata } from '../../../../../viewer/elements/design/design-node/utils/meta-data';
import { CurveDistanceInput } from '../../../../../viewer/tools/utils/context-menu/design-tool/utils/curve';
import { Coordinates } from '../../../../../viewer/utilities/math-helpers';
import { useUnitSymbols } from '../../../../hooks/use-unit-symbols';
import { reset } from '../../../../utils/styled-reset';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Stack } from '../../../stack/stack';
import { CoordinatePropertiesStyled, TOOLTIP_DEFAULT_MIN_WIDTH } from './coordinate-properties';

export type DesignHoverProps = {
  type: 'design';
  isMultiselect: false;
  metaData?: DesignHoverMetadata;
  /** The radius or bulge of a curve being bent, or the radius of a circle, which can be typed. */
  curveDistance?: CurveDistanceInput;
};

export const DesignHoverProperties = (props: {
  coordinate: Coordinates;
  coordinateSystem?: string;
  properties: DesignHoverProps;
}) => {
  const hasAttributes = (props.properties.metaData?.attributes.length ?? 0) > 0;
  const data = props.properties.metaData?.data;
  const curveDistance = props.properties.curveDistance;
  const hasExtraContent =
    data?.type === 'line' ||
    data?.type === 'createline' ||
    data?.type === 'face' ||
    hasAttributes ||
    isDefined(curveDistance);

  return (
    <Component compact={!hasExtraContent} wide={hasAttributes}>
      {curveDistance && <CurveDistanceInfo input={curveDistance} />}
      <HeightInfo coordinateHeight={props.coordinate.height} />
      {(data?.type === 'line' || data?.type === 'createline') && (
        <LineSegmentInfo
          length={data?.length}
          lengthPlanar={data?.planarLength}
          // The line being created goes from its start point, so its slope can go up or down.
          signed={data?.type === 'createline'}
          slope={data?.type === 'createline' ? data.slope : Math.abs(data.slope)}
        />
      )}
      {data?.type === 'face' && (
        <FaceInfo area={data?.area} areaPlanar={data?.planarArea} slope={data?.slope} />
      )}

      {hasAttributes && <AttributesInfo attributes={props.properties.metaData!.attributes} />}
    </Component>
  );
};

/**
 * The radius or bulge of a curve being bent, how far it pokes out from the line between its ends,
 * or the radius of a circle being created. It follows the cursor, and is selected so that a
 * distance can be typed right away, which is applied with Enter. A typed distance that can't be
 * applied, as one that isn't a number or a radius too short for the curve, is marked red and the
 * curve follows the cursor until it is corrected.
 */
const CurveDistanceInfo = (props: { input: CurveDistanceInput }) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  const title =
    props.input.shape === 'circle'
      ? t('assetPositionTooltip.designHoverProperties.title.circle', { ns: 'skyview' })
      : t('assetPositionTooltip.designHoverProperties.title.curve', { ns: 'skyview' });
  const label =
    props.input.kind === 'radius'
      ? t('assetPositionTooltip.designHoverProperties.radius', { ns: 'skyview' })
      : t('assetPositionTooltip.designHoverProperties.curveDistance', { ns: 'skyview' });
  const ref = React.useRef<HTMLInputElement>(null);
  // The typed text, undefined while the distance follows the cursor.
  const [text, setText] = React.useState<string>();
  const distance = props.input.getDistance();
  // The radius of a straight curve
  const value = text ?? (Number.isFinite(distance) ? distance.toFixed(2) : '∞');
  // Empty, it follows the cursor again
  const invalid =
    isDefined(text) && text.trim() !== '' && !isDefined(getValidDistance(props.input, text));

  // Selected while it follows the cursor, so that typing replaces it.
  React.useEffect(() => {
    if (!isDefined(text)) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [text, value]);

  const onChange = (newText: string) => {
    setText(newText);
    props.input.setDistance(getValidDistance(props.input, newText));
  };

  // The keys are for the input, not hotkeys of the tool. Except the modifiers, which change what
  // the tool draws, e.g. Shift that makes the curve follow the lines at its ends.
  const stopUnlessModifier = (e: React.KeyboardEvent) => {
    if (!modifierKeys.includes(e.key)) {
      e.stopPropagation();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    stopUnlessModifier(e);

    if (e.key === 'Enter') {
      if (!invalid) {
        props.input.apply();
      }
    } else if (e.key === 'Escape') {
      props.input.cancel();
    }
  };

  return (
    <Stack spacing={0.5}>
      <LabelledContainer text={title}>
        <P>
          <Label>{label}</Label>
          <DistanceInput
            aria-invalid={invalid}
            aria-label={label}
            inputMode="decimal"
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            onKeyUp={stopUnlessModifier}
          />
          <Unit>{unitSymbols.length}</Unit>
        </P>
      </LabelledContainer>
    </Stack>
  );
};

const modifierKeys = ['Shift', 'Alt', 'Control', 'Meta'];

/** The typed distance, if it is a number that can be applied, with a decimal point or comma. */
const getValidDistance = (input: CurveDistanceInput, text: string) => {
  const distance = Math.abs(Number(text.replace(',', '.')));
  return text.trim() !== '' && Number.isFinite(distance) && (input.isValid?.(distance) ?? true)
    ? distance
    : undefined;
};

/**
 * Displays metadata of a line segment when using design tool
 */
const HeightInfo = (props: { coordinateHeight: number }) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  return (
    <Stack spacing={0.5}>
      <LabelledContainer
        color="#fffa"
        fontSize="0.9em"
        text={t('assetPositionTooltip.designHoverProperties.height', {
          ns: 'skyview',
          unit: unitSymbols.length,
        })}
      >
        <span>
          {props.coordinateHeight.toFixed(2)} {unitSymbols.length}
        </span>
      </LabelledContainer>
    </Stack>
  );
};

const LineSegmentInfo = (props: {
  length: number;
  slope: number;
  lengthPlanar: number;
  signed?: boolean;
}) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  return (
    <Stack spacing={0.5}>
      <LabelledContainer
        text={t('assetPositionTooltip.designHoverProperties.title.lineSegment', {
          ns: 'skyview',
        })}
      >
        <P>
          <Label>
            {t('assetPositionTooltip.designHoverProperties.length3d', { ns: 'skyview' })}
          </Label>
          <span>
            {props.length.toFixed(2)} {unitSymbols.length}
          </span>
        </P>
        <P>
          <Label>
            {t('assetPositionTooltip.designHoverProperties.length2d', { ns: 'skyview' })}
          </Label>
          <span>
            {props.lengthPlanar.toFixed(2)} {unitSymbols.length}
          </span>
        </P>
        <Slope signed={props.signed} slope={props.slope} />
      </LabelledContainer>
    </Stack>
  );
};

/**
 * Displays metadata of a face when using design tool
 */
const FaceInfo = (props: { area: number; areaPlanar: number; slope: number }) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();
  return (
    <Stack spacing={0.5}>
      <LabelledContainer
        text={t('assetPositionTooltip.designHoverProperties.title.triangle', { ns: 'skyview' })}
      >
        <P>
          <Label>
            {t('assetPositionTooltip.designHoverProperties.surface3d', { ns: 'skyview' })}
          </Label>
          <span>
            {props.area.toFixed(2)} {unitSymbols.area}
          </span>
        </P>
        <P>
          <Label>
            {t('assetPositionTooltip.designHoverProperties.surface2d', { ns: 'skyview' })}
          </Label>
          <span>
            {props.areaPlanar.toFixed(2)} {unitSymbols.area}
          </span>
        </P>
        <Slope slope={Math.abs(props.slope)} />
      </LabelledContainer>
    </Stack>
  );
};

/**
 * Displays metadata of a face when using design tool
 */
const AttributesInfo = (props: { attributes: [string, string][] }) => {
  const { t } = useTranslation();
  return (
    <Stack spacing={0.5}>
      <LabelledContainer
        text={t('assetPositionTooltip.designHoverProperties.title.lineSegment', {
          ns: 'skyview',
        })}
      >
        <Table>
          {props.attributes.map((x, idx) => (
            <tr key={idx}>
              <AttributeName>{x[0]}</AttributeName>
              <AttributeValue>{x[1]}</AttributeValue>
            </tr>
          ))}
        </Table>
      </LabelledContainer>
    </Stack>
  );
};

/**
 * @param signed - If true, the slope is shown with + when it goes up and - when it goes down.
 */
export const Slope = (props: { slope: number; signed?: boolean; className?: string }) => {
  const { t } = useTranslation();

  return (
    <P className={props.className} flex={!isDefined(props.className)}>
      <Label>
        {isDefined(props.className)
          ? t('assetPositionTooltip.designHoverProperties.slopeToNextPoint', { ns: 'skyview' })
          : t('assetPositionTooltip.designHoverProperties.slope', { ns: 'skyview' })}
      </Label>
      <span>{formatSlope(props.slope, props.signed)}</span>
    </P>
  );
};

const P = styled.p<{ flex?: boolean }>`
  display: ${(props) => (props.flex ? 'flex' : 'block')};
  align-items: baseline;
`;

const Table = styled.table`
  ${reset}
  width: 100%;
  border-collapse: separate;
  border-spacing: 0.4em;
`;

const AttributeName = styled.td`
  width: 25%;
  color: ${(props) => props.theme.color.gray.medium};
  text-align: left;
`;

const AttributeValue = styled.td`
  text-align: left;
`;

const DistanceInput = styled.input`
  width: 5em;
  padding: 0.1em 0.4em;
  border: 1px solid ${(props) => props.theme.color.brand.dark};
  border-radius: 0.25em;
  outline: none;
  background-color: rgba(255, 255, 255, 0.12);
  color: inherit;
  font: inherit;
  text-align: right;

  &::selection {
    background-color: ${(props) => props.theme.color.brand.dark};
    color: ${(props) => props.theme.color.gray.darkest};
  }

  &[aria-invalid='true'] {
    border-color: ${(props) => props.theme.color.red};
  }
`;

const Unit = styled.span`
  margin-left: 0.3em;
`;

const Label = styled.label<{ width?: number }>`
  width: ${(props) => props.width ?? 50}%;
`;

const Component = styled(CoordinatePropertiesStyled)<{ wide: boolean; compact: boolean }>`
  min-width: ${(props) =>
    props.wide ? '450px' : props.compact ? 'max-content' : TOOLTIP_DEFAULT_MIN_WIDTH};

  p {
    display: flex;
    align-items: baseline;

    label {
      align-items: baseline;
    }
  }

  > p {
    label {
      width: 2em;
    }
  }
`;
